import { useContext, useState } from "react"
import { Modal, ModalBody, ModalHeader } from "flowbite-react"
import { VibratoClientContext } from "./contexts"
import { Search, NavArrowLeft, NavArrowRight } from "iconoir-react"
import { CategorySearchResult } from "@/lib/vibrato-api"

export default function CategorySearchModal({
  show,
  onClose,
  onSelect,
  multiSelect = false,
  selectedCategories = [],
  excludeCategories = []
}: {
  show: boolean
  onClose: () => void
  onSelect: (category: CategorySearchResult) => void
  multiSelect?: boolean
  selectedCategories?: CategorySearchResult[]
  excludeCategories?: CategorySearchResult[]
}) {
  const vibratoClient = useContext(VibratoClientContext)
  const [query, setQuery] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [results, setResults] = useState<CategorySearchResult[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalResults, setTotalResults] = useState(0)
  const [hasSearched, setHasSearched] = useState(false)

  const pageSize = 10

  const performSearch = async (page: number = 1) => {
    if (!vibratoClient || !query.trim()) return

    try {
      setIsLoading(true)
      const resp = await vibratoClient.searchCategories({
        query,
        page,
        page_size: pageSize
      })

      // Filter out excluded categories
      const filteredResults = resp.results.filter(
        (category: CategorySearchResult) => !excludeCategories.some((excluded) => excluded.uuid === category.uuid)
      )

      setResults(filteredResults)
      setTotalResults(resp.total)
      setCurrentPage(page)
      setHasSearched(true)
    } catch (e) {
      console.error("Category search failed", e)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSearch = () => {
    performSearch(1)
  }

  const handleNextPage = () => {
    if (currentPage * pageSize < totalResults) {
      performSearch(currentPage + 1)
    }
  }

  const handlePrevPage = () => {
    if (currentPage > 1) {
      performSearch(currentPage - 1)
    }
  }

  const handleSelect = (category: CategorySearchResult) => {
    onSelect(category)
    if (!multiSelect) {
      onClose()
    }
  }

  const isSelected = (category: CategorySearchResult) => {
    return selectedCategories.some((selected) => selected.uuid === category.uuid)
  }

  const totalPages = Math.ceil(totalResults / pageSize)
  const hasNextPage = currentPage < totalPages
  const hasPrevPage = currentPage > 1

  return (
    <Modal show={show} onClose={onClose} className="pt-safe">
      <ModalHeader className="border-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Search className="w-5 h-5 text-purple-600" />
          </div>
          <span className="text-xl font-semibold text-gray-900">
            {multiSelect ? "Search Categories" : "Select Category"}
          </span>
        </div>
      </ModalHeader>
      <ModalBody>
        <div className="flex flex-col gap-6">
          {/* Search Input */}
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Category Name</label>
              <div className="relative">
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  placeholder="Enter category name (e.g., Restaurant, Retail)"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                />
              </div>
            </div>
            <div className="flex justify-center">
              <button
                onClick={handleSearch}
                disabled={!query.trim() || isLoading}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center gap-2"
              >
                <Search className="w-5 h-5" />
                {isLoading ? "Searching..." : "Search Categories"}
              </button>
            </div>
          </div>

          {/* Search Results */}
          {hasSearched && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Search Results</h3>
                {totalResults > 0 && (
                  <span className="text-sm text-gray-500">
                    {totalResults} result{totalResults !== 1 ? "s" : ""} found
                  </span>
                )}
              </div>

              {isLoading ? (
                <div className="flex flex-col items-center justify-center h-40 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-3" />
                  <p className="text-gray-600 text-sm">Searching categories...</p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    {results.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-32 bg-gray-50 rounded-xl border border-gray-200">
                        <Search className="w-8 h-8 text-gray-400 mb-2" />
                        <p className="text-gray-500 text-sm">No categories found</p>
                        <p className="text-xs text-gray-400 mt-1">Try a different search term</p>
                      </div>
                    ) : (
                      results.map((category) => (
                        <button
                          key={category.uuid}
                          onClick={() => handleSelect(category)}
                          disabled={isSelected(category)}
                          className={`w-full text-left p-4 border rounded-xl transition-all duration-200 group ${
                            isSelected(category)
                              ? "border-green-300 bg-green-50 cursor-not-allowed"
                              : "bg-white border-gray-200 hover:border-purple-300 hover:bg-purple-50"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                              <div
                                className={`font-semibold mb-1 ${
                                  isSelected(category) ? "text-green-900" : "text-gray-900 group-hover:text-purple-900"
                                }`}
                              >
                                {category.name}
                                {isSelected(category) && (
                                  <span className="ml-2 text-sm text-green-600 font-normal">✓ Selected</span>
                                )}
                              </div>
                              {category.description && (
                                <div className="text-sm text-gray-600 line-clamp-2">{category.description}</div>
                              )}
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <button
                        onClick={handlePrevPage}
                        disabled={!hasPrevPage}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <NavArrowLeft className="w-4 h-4" />
                        Previous
                      </button>

                      <span className="text-sm text-gray-700">
                        Page {currentPage} of {totalPages}
                      </span>

                      <button
                        onClick={handleNextPage}
                        disabled={!hasNextPage}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                        <NavArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </>
              )}

              {multiSelect && selectedCategories.length > 0 && (
                <div className="text-center pt-4 border-t border-gray-100">
                  <button
                    onClick={onClose}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
                  >
                    Done ({selectedCategories.length} selected)
                  </button>
                </div>
              )}

              <div className="text-center pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  Can't find the right category? Email us at{" "}
                  <a
                    href="mailto:contact@getvibrato.com?subject=Category Request"
                    className="text-purple-600 hover:text-purple-700 font-medium underline"
                  >
                    contact@getvibrato.com
                  </a>
                </p>
              </div>
            </div>
          )}
        </div>
      </ModalBody>
    </Modal>
  )
}
